/**
 * Workflow v2 — action creators.
 *
 * Wraps the reducer's `dispatch` in named functions so consumers
 * call `actions.hydrateChat(stepId, messages)` instead of
 * `dispatch({ type: ACTIONS.HYDRATE_CHAT, stepId, messages })`.
 * Three reasons:
 *
 *   1. Readability — call sites read like business intent, not
 *      action-type plumbing.
 *   2. Refactor safety — renaming an action only requires updating
 *      this file, not every dispatch site.
 *   3. Cross-cutting hooks — a single layer to add telemetry,
 *      devtools, or optimistic-update wrappers without touching
 *      consumers.
 *
 * Usage:
 *   const actions = useMemo(() => createWorkflowActions(dispatch), [dispatch]);
 *   ...
 *   onClick={() => actions.selectStep(2)}
 *
 * The orchestrator (WorkflowV2.tsx) creates `actions` once and
 * passes it through `<WorkflowV2AppShell actions={actions}>` to
 * the rail / action panel / step shell.
 */
import { ACTIONS } from './workflowV2Reducer';
import type {
  BackendWorkflowProgress,
  ChatMessage,
  StepStatus,
  Step0Mode,
  WorkflowActions,
} from './workflowV2Types';

type Dispatch = (action: any) => void;

export function createWorkflowActions(dispatch: Dispatch): WorkflowActions {
  return {
    selectStep:        (stepId: number) => dispatch({ type: ACTIONS.SELECT_STEP, stepId }),
    hydrate:           (workflowProgress: BackendWorkflowProgress) => dispatch({ type: ACTIONS.HYDRATE_FROM_BACKEND, workflowProgress }),
    setStepStatus:     (stepId: number, status: StepStatus, error: string | null = null) => dispatch({ type: ACTIONS.SET_STEP_STATUS, stepId, status, error }),
    setStepResult:     (stepId: number, result: unknown) => dispatch({ type: ACTIONS.SET_STEP_RESULT, stepId, result }),
    setStepMode:       (stepId: number, mode: Step0Mode | null) => dispatch({ type: ACTIONS.SET_STEP_MODE, stepId, mode }),
    hydrateChat:       (stepId: number, messages: ChatMessage[]) => dispatch({ type: ACTIONS.HYDRATE_CHAT, stepId, messages }),
    updateDraft:       (stepId: number, draft: string) => dispatch({ type: ACTIONS.UPDATE_DRAFT, stepId, draft }),
    sendUserMessage:   (stepId: number) => dispatch({ type: ACTIONS.SEND_USER_MESSAGE, stepId }),
    receiveAgentReply: (stepId: number, text: string) => dispatch({ type: ACTIONS.RECEIVE_AGENT_REPLY, stepId, text }),
    // Hierarchical modifications (Story H2).
    setHierRelevance:    (stepId: number, recordId: string, isRelevant: boolean) =>
      dispatch({ type: ACTIONS.SET_HIER_RELEVANCE, stepId, recordId, isRelevant }),
    setHierJustification:(stepId: number, recordId: string, justification: string) =>
      dispatch({ type: ACTIONS.SET_HIER_JUSTIFICATION, stepId, recordId, justification }),
    clearHierModifications:(stepId: number) =>
      dispatch({ type: ACTIONS.CLEAR_HIER_MODIFICATIONS, stepId }),
    resetStep:         (stepId: number) => dispatch({ type: ACTIONS.RESET_STEP, stepId }),
    resetAll:          () => dispatch({ type: ACTIONS.RESET_ALL }),
  };
}
